import { handleResponse, handleError, headerset } from "../apiUtils";
import {FaseFrazione, FrazioneOdP} from "components/main/schedulazione/frazioniOdPSlice";
import { FiltriFrazioniOdP } from "components/userPreference/userPreferenceSlice";

// ============================================
// BASE URL
// ============================================

const baseUrl = (frazioneOdPId?: number | null) => {
    let url: string = process.env.API_URL + "/api/schedulazione/frazioni/";

    if (frazioneOdPId) {
        url += frazioneOdPId + "/";
    }

    return url;
};

// ============================================
// QUERY STRING BUILDER
// ============================================

const buildQueryString = (filtri: FiltriFrazioniOdP): string => {
    const params = new URLSearchParams();

    // ========== FILTRI SPECIFICI ==========

    // Filtro OdP di riferimento
    if (filtri.odp) {
        params.append("odp", filtri.odp.toString());
    }

    if (filtri.stati && filtri.stati.length > 0) {
        params.append("stati", filtri.stati.join(","));
    }

    if (filtri.reparto && filtri.reparto.length > 0) {
        params.append("reparto", filtri.reparto.join(","));
    }

    if (filtri.data_da) {
        params.append("data_da", filtri.data_da);
    }

    if (filtri.data_a) {
        params.append("data_a", filtri.data_a);
    }

    // ========== PAGINAZIONE E RICERCA (sempre presenti) ==========

    if (filtri.page) {
        params.append("page", filtri.page.toString());
    }
    if (filtri.numero_record) {
        params.append("numero_record", filtri.numero_record.toString());
    }
    if (filtri.search) {
        params.append("search", filtri.search);
    }

    return params.toString();
};

// ============================================
// API FUNCTIONS
// ============================================

/**
 * Fetch lista paginata di Frazioni OdP
 */
export function fetchFrazioniOdP(filtri: FiltriFrazioniOdP = {}) {
    let url = baseUrl();
    const queryString = buildQueryString(filtri);
    if (queryString) {
        url += `?${queryString}`;
    }

    return fetch(url, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: headerset().Authorization,
        },
    })
        .then(handleResponse)
        .catch(handleError);
}

/**
 * Get singola Frazione OdP by ID (con fasi nestate)
 */
export function getFrazioneOdP(frazioneOdPId: number) {
    const url = baseUrl(frazioneOdPId);

    return fetch(url, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: headerset().Authorization,
        },
    })
        .then(handleResponse)
        .catch(handleError);
}

/**
 * Save (create/update) Frazione OdP
 */
export function saveFrazioneOdP(frazioneOdPToSave: FrazioneOdP) {
    return fetch(baseUrl(frazioneOdPToSave.id), {
        method: frazioneOdPToSave.id ? "PUT" : "POST",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: headerset().Authorization,
        },
        body: JSON.stringify({
            ...frazioneOdPToSave,
            // Pulire campi display/computed prima dell'invio
            odp_display: undefined,
            stato_display: undefined,
            percentuale_avanzamento: undefined,
            ha_ostacoli_attivi: undefined,
            fasi: undefined, // Le fasi si gestiscono separatamente
            errorsStack: undefined,
        }),
    })
        .then(handleResponse)
        .catch(handleError);
}

/**
 * Delete Frazione OdP
 */
export function deleteFrazioneOdP(frazioneOdPToDelete: FrazioneOdP) {
    return fetch(baseUrl(frazioneOdPToDelete.id), {
        method: "DELETE",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: headerset().Authorization,
        },
    })
        .then(handleResponse)
        .catch(handleError);
}

/**
 * Gestione D&D
 */
export function schedulaFrazione(odpId: number, data: string) {
    const url:string=`${baseUrl()}${odpId}/schedula_frazione/${data}/`;
    return fetch(url, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: headerset().Authorization,
        },
        // body: JSON.stringify({
        //     ...frazioneOdPToSave,
        //     // Pulire campi display/computed prima dell'invio
        //     odp_display: undefined,
        //     stato_display: undefined,
        //     percentuale_avanzamento: undefined,
        //     ha_ostacoli_attivi: undefined,
        //     fasi: undefined, // Le fasi si gestiscono separatamente
        //     errorsStack: undefined,
        // }),
    })
        .then(handleResponse)
        .catch(handleError);
}


// ============================================
// API FUNCTIONS - FASI FRAZIONE
// ============================================

const fasiBaseUrl = (frazioneId: number, faseId?: number | null) => {
    let url: string = baseUrl(frazioneId) + "fasi/";

    if (faseId) {
        url += faseId + "/";
    }

    return url;
};
/**
 * Save (create/update) Fase Frazione
 * @param frazioneId - ID della frazione padre
 * @param faseFrazioneToSave - dati della fase
 * @param azione - azione opzionale passata come query string
 */
export function saveFaseFrazione(
    frazioneId: number,
    faseFrazioneToSave: FaseFrazione,
    azione?: string
) {
    let url = fasiBaseUrl(frazioneId, faseFrazioneToSave.id);

    if (azione) {
        url += `?azione=${encodeURIComponent(azione)}`;
    }

    return fetch(url, {
        method: faseFrazioneToSave.id ? "PUT" : "POST",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: headerset().Authorization,
        },
        body: JSON.stringify({
            ...faseFrazioneToSave,
            // Pulire campi display/computed prima dell'invio
            fase_legacy_display: undefined,
            fase_legacy_codice_lavorazione: undefined,
            fase_legacy_numero_fase: undefined,
            reparto_display: undefined,
            stato_display: undefined,
            ore_residue: undefined,
            percentuale_avanzamento: undefined,
            ha_ostacoli_attivi: undefined,
            is_bloccata: undefined,
            errorsStack: undefined,
        }),
    })
        .then(handleResponse)
        .catch(handleError);
}

/**
 * Delete Fase Frazione
 * @param frazioneId - ID della frazione padre
 * @param faseFrazioneToDelete - fase da eliminare
 */
/**
 * Rischedulazione Frazione intera (slittamento in blocco)
 * Endpoint dedicato: PATCH frazioni/<id>/rischedulazione/<nuova_data_inizio>/
 * Sposta tutte le fasi mantenendo le durate lavorative originali
 */
export function rischedulaFrazione(
    frazioneId: number,
    nuovaDataInizio: string
) {
    const url = `${baseUrl()}${frazioneId}/rischedulazione/${nuovaDataInizio}/`;
    return fetch(url, {
        method: "PATCH",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: headerset().Authorization,
        },
    })
        .then(handleResponse)
        .catch(handleError);
}

/**
 * Rischedulazione Fase Frazione (resize o spostamento)
 * Endpoint dedicato: PATCH fasi_frazioni/<id>/rischedulazione/
 * Accetta una o entrambe le date
 */
export function rischedulaFaseFrazione(
    faseFrazioneId: number,
    date: { data_inizio_pianificata?: string; data_fine_pianificata?: string }
) {
    const url = `${process.env.API_URL}/api/schedulazione/fasi_frazioni/${faseFrazioneId}/rischedulazione/`;
    return fetch(url, {
        method: "PATCH",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: headerset().Authorization,
        },
        body: JSON.stringify(date),
    })
        .then(handleResponse)
        .catch(handleError);
}

/**
 * Delete Fase Frazione
 * @param frazioneId - ID della frazione padre
 * @param faseFrazioneToDelete - fase da eliminare
 */
export function deleteFaseFrazione(
    frazioneId: number,
    faseFrazioneToDelete: FaseFrazione
) {
    return fetch(fasiBaseUrl(frazioneId, faseFrazioneToDelete.id), {
        method: "DELETE",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: headerset().Authorization,
        },
    })
        .then(handleResponse)
        .catch(handleError);
}